import SecureLS from "secure-ls";
import { Auth } from "aws-amplify";

import { currentCountryVar, currentUserVar } from "@apollo";
import { User } from "@models";
import {
  getAffiliateServiceArea,
  fetchSignInData,
  getBonusSatsAmount,
  isServerSideRendering,
  LocaleUtils,
} from "@utils";

const refreshLocale = async (userCountrySavedBeforeRefresh: string): Promise<string> => {
  if (userCountrySavedBeforeRefresh) {
    const userCountry: AffiliateServiceArea = getAffiliateServiceArea(
      userCountrySavedBeforeRefresh,
    );

    localStorage.setItem("userCountry", userCountry.countryCode);
    currentCountryVar(userCountry);
    localStorage.removeItem("userCountrySavedBeforeRefresh");

    return userCountry.currencyCode;
  }

  const { currencyCode } = await LocaleUtils.setLocale();
  return currencyCode;
};

const isAuthFailure = (error: Error): boolean =>
  /unauthorized|not authorized|no current user|401|jwt|token/i.test(
    String(error?.message || error),
  );

const clearStaleAuthState = async (encryptedLocalStorage: SecureLS): Promise<void> => {
  encryptedLocalStorage.remove("persisted-user");
  currentUserVar(null);

  try {
    await Auth.signOut();
  } catch {
    return;
  }
};

/*
This function is called on every startup of the app and is responsible for:
  - Assigning the current AffiliateServiceArea to the currentCountry Apollo reactive var
  - Ensuring a language value is set in localStorage (defaults to "en" if not set)
  - If a user was previously signed in, validating the Cognito session before
    assigning their encrypted data to the currentUser Apollo reactive var
  - Initializing the current bonus sats amount var from SSM
*/
export const initializeAppStartup = async (): Promise<void> => {
  if (isServerSideRendering()) return;

  (async function initBonusSatsVar() {
    await getBonusSatsAmount();
  })();

  // Updates wallet if page has just been refreshed due to a user changing country and language at the same time
  const userCountrySavedBeforeRefresh: string = localStorage.getItem(
    "userCountrySavedBeforeRefresh",
  );

  const encryptedLocalStorage = new SecureLS();
  const persistedUser: User = encryptedLocalStorage.get("persisted-user");

  if (persistedUser) {
    try {
      // Auth.currentSession refreshes valid Cognito sessions and rejects stale
      // local storage left behind by deploy/env changes.
      await Auth.currentSession();
    } catch {
      await clearStaleAuthState(encryptedLocalStorage);
      await refreshLocale(userCountrySavedBeforeRefresh);
      return;
    }

    currentUserVar(persistedUser);
    const currencyCode = await refreshLocale(userCountrySavedBeforeRefresh);

    try {
      const user = await fetchSignInData(currencyCode);
      encryptedLocalStorage.set("persisted-user", user);
      currentUserVar(user);
    } catch (error) {
      if (isAuthFailure(error)) {
        await clearStaleAuthState(encryptedLocalStorage);
      }
    }
  } else {
    await refreshLocale(userCountrySavedBeforeRefresh);
  }
};
